import { useMemo } from 'react';
import { Box, Typography, Stack, Chip } from '@mui/material';
import { extractValues, descriptiveStats, computeAQI, sensorKeyToPollutant } from '../statistics';
import { useTimeline } from '../TimelineContext';

function AQIGauge({ aqi, category, color }) {
  const pct = Math.min(aqi / 500, 1);
  return (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Box sx={{ position: 'relative', width: 120, height: 120, mx: 'auto' }}>
        <svg viewBox="0 0 120 120" width="120" height="120">
          {/* Background arc */}
          <circle cx="60" cy="60" r="50" fill="none" stroke="#eee" strokeWidth="12"
            strokeDasharray="235.6" strokeDashoffset="0"
            transform="rotate(135 60 60)" strokeLinecap="round" />
          {/* Value arc */}
          <circle cx="60" cy="60" r="50" fill="none" stroke={color} strokeWidth="12"
            strokeDasharray={`${pct * 235.6} 235.6`} strokeDashoffset="0"
            transform="rotate(135 60 60)" strokeLinecap="round" />
        </svg>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h4" fontWeight="bold" sx={{ color, lineHeight: 1 }}>{aqi}</Typography>
          <Typography variant="caption" color="text.secondary">AQI</Typography>
        </Box>
      </Box>
      <Chip label={category} size="small" sx={{ mt: 0.5, bgcolor: color, color: '#fff', fontWeight: 'bold' }} />
    </Box>
  );
}

export default function AQIWidget({ recordings = [], widgetConfig }) {
  const { window: timeWindow } = useTimeline();
  const recId = widgetConfig?.recId || '';
  const sensorKey = widgetConfig?.sensorKey || '';

  const rec = recordings.find((r) => r.id === recId);

  // Only show AQI-compatible sensors
  const aqiSensorKeys = useMemo(() => {
    if (!rec?.data) return [];
    return Object.keys(rec.data).filter((k) => sensorKeyToPollutant(k) !== null);
  }, [rec]);

  const result = useMemo(() => {
    if (!rec || !sensorKey) return null;
    const pollutant = sensorKeyToPollutant(sensorKey);
    if (!pollutant) return null;

    const values = extractValues(rec, sensorKey, timeWindow?.start, timeWindow?.end);
    if (values.length === 0) return null;
    const stats = descriptiveStats(values);
    if (!stats) return null;

    const currentAQI = computeAQI(pollutant, stats.mean);
    const maxAQI = computeAQI(pollutant, stats.max);
    const minAQI = computeAQI(pollutant, stats.min);

    return { pollutant, current: currentAQI, max: maxAQI, min: minAQI, mean: stats.mean, count: stats.count };
  }, [rec, sensorKey, timeWindow]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {result ? (
        <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />}
          <AQIGauge aqi={result.current.aqi} category={result.current.category} color={result.current.color} />
          <Typography variant="caption" color="text.secondary">Mean: {result.mean.toFixed(1)} ({result.pollutant.toUpperCase()})</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
            <Stack alignItems="center">
              <Typography variant="caption" fontWeight="bold" sx={{ color: result.min.color }}>{result.min.aqi}</Typography>
              <Typography variant="caption" color="text.secondary">Min AQI</Typography>
            </Stack>
            <Stack alignItems="center">
              <Typography variant="caption" fontWeight="bold" sx={{ color: result.max.color }}>{result.max.aqi}</Typography>
              <Typography variant="caption" color="text.secondary">Max AQI</Typography>
            </Stack>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>n={result.count}</Typography>
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {aqiSensorKeys.length === 0 && rec ? 'No AQI-compatible sensors (PM2.5, PM10, CO2)' : 'Select a recording and pollutant'}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
